/**
 * Date-window helpers for the "Offene Prüfungen" dashboard view (TES-506).
 *
 * Isolated, side-effect-free logic so the 14-day-window edge cases
 * (NULL due_date, timezone/weekend boundaries) are unit-testable without
 * rendering the panel. Reales Schema ist Gesetz (D-CHK-015):
 *  - `inspection_status` ∈ pending · in_progress · completed · overdue · cancelled
 *  - "nicht zugeordnet" = `assigned_to IS NULL` (KEIN Status-Enum-Wert)
 */

import type { Database } from "@/integrations/supabase/types";

export type InspectionStatus = Database["public"]["Enums"]["inspection_status"];

/** Default look-ahead window for the "next N days" overview card. */
export const OPEN_INSPECTIONS_WINDOW_DAYS = 14;

/**
 * Statuses considered "open" (i.e. actionable / not yet finished).
 * `completed` and `cancelled` are explicitly excluded — `cancelled` is shown
 * separately/flagged, never counted as open.
 */
export const OPEN_INSPECTION_STATUSES: readonly InspectionStatus[] = [
  "pending",
  "in_progress",
  "overdue",
] as const;

/**
 * Inclusive ISO bounds of the `[now, now + windowDays]` window.
 * The upper bound is the END of the last calendar day (23:59:59.999 local),
 * so an inspection due late on day +N is still inside the window regardless of
 * the time-of-day component or DST shifts within the range.
 *
 * Internal helper of {@link isDueWithinWindow} — not part of the module's public
 * surface (tested through `isDueWithinWindow`).
 */
interface DueWindow {
  /** Lower bound — `now` (inclusive). */
  fromIso: string;
  /** Upper bound — end of the (now + windowDays)-th day, inclusive. */
  toIso: string;
}

function getDueWindow(
  now: Date = new Date(),
  windowDays: number = OPEN_INSPECTIONS_WINDOW_DAYS,
): DueWindow {
  const from = new Date(now.getTime());
  const to = new Date(now.getTime());
  // Advance by the window length, then snap to the end of that local day so
  // weekend/timezone time-of-day differences never clip a valid due date.
  to.setDate(to.getDate() + windowDays);
  to.setHours(23, 59, 59, 999);
  return { fromIso: from.toISOString(), toIso: to.toISOString() };
}

/**
 * True when `dueDate` falls inside `[now, now + windowDays]`.
 *
 * Edge handling:
 *  - NULL / empty `dueDate` → `false` (no due date = not "due in the next N days").
 *  - Unparseable date → `false` (defensive; never throws).
 *  - `dueDate` strictly in the past (overdue) → `false` for THIS window check —
 *    overdue items are surfaced via status, not via the look-ahead window.
 */
export function isDueWithinWindow(
  dueDate: string | null | undefined,
  now: Date = new Date(),
  windowDays: number = OPEN_INSPECTIONS_WINDOW_DAYS,
): boolean {
  if (!dueDate) return false;
  const due = new Date(dueDate);
  if (Number.isNaN(due.getTime())) return false;

  const { fromIso, toIso } = getDueWindow(now, windowDays);
  const dueMs = due.getTime();
  return dueMs >= new Date(fromIso).getTime() && dueMs <= new Date(toIso).getTime();
}
